// @ts-check
// import VieVinumStencil from './VieVinumStencil';
import { gql } from '@apollo/client';
import { Circle, Image, Line, Svg } from '@react-pdf/renderer';
import React from 'react';
import {
  Box,
  Page,
  Registration,
  Text,
  Title,
} from '../../../pdf-design-system';
import {
  PageProvider,
  PagesProvider,
  usePage,
} from '../../../pdf-design-system/Base/PageLayout/Page';
import UserLocaleProvider from '../../../Providers/UserLocaleProvider';
import StaticCatalogPageContent from '../Catalog/StaticCatalogPageContent';
import VieVinumTable from '../Catalog/VieVinumTable';
import dinLight from '../fonts/300.js';
import dinMedium from '../fonts/500.js';
import getStands from '../getStands';
import messages from '../locale/messages';
import { StaticMyListPageContent } from '../MyList/VieVinumMyList';
import VieVinumMyListTable from '../MyList/VieVinumMyListTable';
import { RoomProvider } from '../useRoom';

export default function VieVinumPreview({ data, locale, ...rest }) {
  const { stands, room } = afterQuery(data);
  return (
    <UserLocaleProvider locale={locale} messages={messages}>
      <PagesProvider>
        {[...Array(Math.ceil(stands.length / 4))].map((_, index) => (
          <RoomProvider key={index} value={room}>
            <Sheet>
              <Box style={{ color: '#fff', marginBottom: '5mm' }}>
                <Title variant="large">VieVinum 2022 – Druckvorschau</Title>
                <Text style={{ width: '50%', marginBottom: '3mm' }}>
                  Vielen Dank für die Dateneingabe zur VieVinum 2022! Die
                  folgenden Seiten sollen dir einen Überblick geben, wie dein
                  Betrieb und deine Produkte im Katalog und Verkostungsheft
                  dargestellt werden.
                </Text>
                <Text>
                  Die erste Seite soll dir einen Überblick geben, sie ist nicht
                  zum Ausdrucken gedacht.
                </Text>
                <Text>Alle folgenden Seiten kannst du auf A4 ausdrucken.</Text>
              </Box>
              <CatalogPreview stands={stands.slice(index * 4, index * 4 + 4)} />
              {[...Array(Math.ceil(stands.length / 2))].map((_, index) => (
                <MyListPreview
                  key={index}
                  stands={stands.slice(index * 2, index * 2 + 2)}
                />
              ))}
            </Sheet>
            <CatalogPageA4 stands={stands.slice(index * 4, index * 4 + 4)} />
            <MyListPageA4 stands={stands.slice(index * 4, index * 4 + 4)} />
            <Premiumeintrag stand={stands[index * 4]} />
          </RoomProvider>
        ))}
      </PagesProvider>
    </UserLocaleProvider>
  );
}

/** @param {{ children: React.ReactNode; bleed?: number; style?: import('@react-pdf/types').Style; [x: string]: any }} props */
function Sheet({ children, bleed = 0, style, ...rest }) {
  return (
    <Page
      size={{
        width: `${5 + 162 + 10 + 162 + 5}mm`,
        height: `${5 + 50 + 30 + 234 + 30 + 297 + 5}mm`,
      }}
      style={[
        { padding: '5mm', backgroundColor: theme.colors.background },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {children}
    </Page>
  );
}

/** @param  {{ stands: ReturnType<afterQuery>["stands"] }} props */
function CatalogPreview({ stands }) {
  return (
    <>
      <Box style={{ color: '#fff', marginBottom: '5mm' }}>
        <Title style={{ marginTop: '5mm' }}>1. Katalogvorschau</Title>
        <Text>
          So sieht dein Eintrag auf der linken oder rechten Seite im
          Druckkatalog aus:
        </Text>
      </Box>
      <Box
        style={{
          position: 'relative',
          width: '100%',
          height: '234mm',
          overflow: 'hidden',
        }}
      >
        <Box style={{ display: 'flex', flexDirection: 'row' }}>
          <PageProvider value={2}>
            <SingleCatalogPage stands={stands} />
          </PageProvider>
          <Box style={{ width: '10mm' }} />
          <PageProvider value={3}>
            <SingleCatalogPage stands={stands} />
          </PageProvider>
        </Box>
        <Rings />
      </Box>
    </>
  );
}

function Rings() {
  return (
    <Box
      style={{
        position: 'absolute',
        left: '162mm',
        width: '10mm',
        paddingTop: '3mm',
      }}
    >
      {Array.from(Array(15)).map((_, index) => (
        <Ring key={index} />
      ))}
    </Box>
  );
}
function Ring() {
  return (
    <Svg
      viewBox="0 0 30 5"
      style={{
        height: '5mm',
        width: '30mm',
        marginVertical: '5mm',
        marginHorizontal: '-10mm',
        zIndex: 1,
      }}
    >
      <Circle cx={2.5} cy={2.5} r={2.5} fill="#555" />
      <Circle cx={27.5} cy={2.5} r={2.5} fill="#555" />
      <Line x1={2.5} y1={1.5} x2={27.5} y2={1.5} stroke="#ddd" />
      <Line x1={2.5} y1={3.5} x2={27.5} y2={3.5} stroke="#ddd" />
    </Svg>
  );
}

/** @param  {{ stands: ReturnType<afterQuery>["stands"] }} props */
function SingleCatalogPage({ stands }) {
  const { alignment } = usePage();
  const padding =
    alignment === 'left' ? { paddingRight: '6mm' } : { paddingLeft: '6mm' };

  return (
    <Box
      style={{
        width: '162mm',
        height: '234mm',
        backgroundColor: '#fff',
        paddingHorizontal: '9mm',
        paddingTop: '8mm',
        paddingBottom: '7mm',
      }}
    >
      <StaticCatalogPageContent>
        {/* Table before the winery */}
        {stands.length < 3 && (
          <Registration.Provider
            value={{ name: 'Voriges Weingut', profile: {} }}
          >
            <VieVinumTable style={[padding, { opacity: 0.3 }]} />
          </Registration.Provider>
        )}
        {stands.map((stand) => (
          <Registration.Provider key={stand.key} value={stand.registration}>
            <VieVinumTable style={padding} />
          </Registration.Provider>
        ))}
        {/* Tables after the winery */}
        {stands.length < 4 && (
          <Registration.Provider
            value={{ name: 'Nächstes Weingut', profile: {} }}
          >
            <VieVinumTable style={[padding, { opacity: 0.3 }]} />
          </Registration.Provider>
        )}
        {stands.length < 2 && (
          <Registration.Provider
            value={{ name: 'Übernächstes Weingut', profile: {} }}
          >
            <VieVinumTable style={[padding, { opacity: 0.3 }]} />
          </Registration.Provider>
        )}
      </StaticCatalogPageContent>
    </Box>
  );
}

/** @param  {{ stands: ReturnType<afterQuery>["stands"] }} props */
function CatalogPageA4({ stands }) {
  return (
    <Page size="A4" theme={theme}>
      <Box
        style={{
          width: '162mm',
          height: '234mm',
          marginTop: '30mm',
          marginHorizontal: 'auto',
          paddingHorizontal: '9mm',
          paddingVertical: '8mm',
          borderWidth: 1,
          borderStyle: 'dashed',
          overflow: 'hidden',
        }}
      >
        <StaticCatalogPageContent>
          {stands.map((stand) => (
            <Registration.Provider key={stand.key} value={stand.registration}>
              <VieVinumTable style={{ paddingRight: '6mm' }} />
            </Registration.Provider>
          ))}
        </StaticCatalogPageContent>
      </Box>
    </Page>
  );
}

/** @param  {{ stands: ReturnType<afterQuery>["stands"] }} props */
function MyListPageA4({ stands }) {
  return (
    <Page size="A4" theme={theme}>
      <StaticMyListPageContent>
        {stands.map((stand) => (
          <Registration.Provider key={stand.key} value={stand}>
            <VieVinumMyListTable />
          </Registration.Provider>
        ))}
        {stands.length === 1 &&
          stands.map((stand) => (
            <Registration.Provider key={stand.key} value={stand}>
              <VieVinumMyListTable />
            </Registration.Provider>
          ))}
      </StaticMyListPageContent>
    </Page>
  );
}

/** @param  {{ stands: ReturnType<afterQuery>["stands"] }} props */
function MyListPreview({ stands }) {
  return (
    <Box style={{ paddingVertical: '5mm' }}>
      <Box
        style={{
          width: '210mm',
          marginHorizontal: 'auto',
          color: '#fff',
          marginBottom: '5mm',
        }}
      >
        <Title style={{ marginTop: '5mm' }}>Persönliches Verkostungsheft</Title>
        <Text>
          Besucher planen online den Messebesuch und können aus ihrer Merkliste
          ihr persönliches Verkostungsheft erzeugen.
          {'\n'}So sieht dein Eintrag aus:
        </Text>
      </Box>
      <Box
        style={{
          width: '210mm',
          height: '297mm',
          marginHorizontal: 'auto',
          backgroundColor: 'white',
        }}
      >
        <StaticMyListPageContent>
          {stands.slice(0, 2).map((stand) => (
            <Registration.Provider key={stand.key} value={stand}>
              <VieVinumMyListTable />
            </Registration.Provider>
          ))}
          {stands.length === 1 && (
            <Registration.Provider
              value={{
                registration: {
                  name: 'Nächster Winzer',
                },
                registeredProducts: [],
              }}
            >
              <VieVinumMyListTable style={{ opacity: 0.3 }} />
            </Registration.Provider>
          )}
        </StaticMyListPageContent>
      </Box>
    </Box>
  );
}

/** @param {{ stand: ReturnType<afterQuery>[number] }} param */
function Premiumeintrag({ stand }) {
  if (!stand.hatPremiumeintrag) return <PremiumeintragAd />;
  return (
    <Page size="A4" style={{ padding: '20mm' }} theme={theme}>
      <Title>Vielen Dank für deine Premiumeintrag-Buchung!</Title>
      <Text variant="p">
        Optimiere deinen VieVinum-Messeauftritt online und im Katalog mit diesen
        Tipps vom unserwein-Team:
      </Text>

      <Box style={{ display: 'flex', flexDirection: 'row' }}>
        <Box style={{ width: '100mm' }}>
          <Text variant="semiTitle">Onlineshop verlinken</Text>
          <Text>Bei jedem Wein gibt es ein Feld "Onlineshop".</Text>
          <Text variant="p">
            Verlinke jeden Wein mit der entsprechenden Seite im Onlineshop, wir
            zeigen dann einen Kaufbutton beim Wein an.
          </Text>

          <Text variant="semiTitle">Auszeichnungen ergänzen</Text>
          <Text variant="p">
            Ergänze Auszeichnungen bei jedem Wein. Wir stellen sie online und im
            Verkostungsheft dar.
          </Text>
        </Box>
        <Image
          src="https://res.cloudinary.com/bottlebooks/image/upload/v2/vievinum/premiumpaket-1.png"
          style={{
            height: '57mm',
            width: '70mm',
            margin: '3mm',
            marginRight: 0,
            padding: '1mm',
            borderWidth: 1,
          }}
        />
      </Box>

      <Text variant="semiTitle">Matchmaking nutzen</Text>
      <Text>Nutze die Terminbuchung mit Fachbesuchern im Matchmaking.</Text>
      <Text>
        Beantworte unbedingt die Frage "ich nehme kostenlos am Matchmaking teil"
        mit ja. Du erhältst damit den Zugang zur Fachbesucher-Liste und kannst
        Terminanfragen buchen und erhalten.
      </Text>
      <Text variant="p">
        Fülle möglichst alle Daten, wie z.B. ein Foto für das Matchmaking. Du
        kannst deine Daten auch noch in b2match ergänzen, aber du sparst dir
        später Aufwand, wenn du jetzt schon vollständige Daten angibst.
      </Text>
      <Text variant="p">
        Wir legen alle deine Weine für dich im Matchmaking-Tool von b2match an,
        damit du für Fachbesucher über möglichst viele Wege gefunden wirst.
      </Text>
      <Text variant="semiTitle">
        Bonus: ProWein-Web&amp;App-Plus-Paket gratis
      </Text>
      <Text>
        Profitiere doppelt, falls du auch an der ProWein teilnimmst: Das
        Web&amp;App-Plus-Paket im Wert von 199€ ist kostenlos im Premiumeintrag
        inkludiert. Gib uns kurz per E-Mail Bescheid, und wir schalten dich
        frei.
      </Text>
      <Text>
        Mehr Infos zum Paket auf der ProWein-Homepage unter
        https://oos.prowein.de/de/BOTT_BOT003
      </Text>

      <Text style={{ marginTop: '5mm' }}>
        Bei Fragen wende dich an unser Team: Kurzes Mail an
        bernhard@unserwein.at genügt, wir helfen dir gerne!
      </Text>
    </Page>
  );
}

function PremiumeintragAd() {
  return (
    <Page size="A4" style={{ padding: '20mm' }} theme={theme}>
      <Title>Mach mehr aus deinem VieVinum-Auftritt!</Title>
      <Text>
        Mit einem unserwein-Premiumeintrag kannst du deinen
        VieVinum-Messeauftritt optimieren und dich von den anderen Ausstellern
        abheben. Das sind einige der Vorteile:
      </Text>
      <Box style={{ display: 'flex', flexDirection: 'row' }}>
        <Box style={{ width: '100mm' }}>
          <Text variant="semiTitle">Onlineshop verlinken</Text>
          <Text variant="p">
            Verlinke jeden Wein mit deinem Onlineshop und hilf den
            Messebesuchern, deine Weine so einfach wie möglich ab Hof zu
            bestellen.
          </Text>

          <Text variant="semiTitle">Auszeichnungen</Text>
          <Text variant="p">
            Ergänze Auszeichnungen bei jedem Wein. Wir stellen sie online und im
            Verkostungsheft dar.
          </Text>
        </Box>
        <Image
          src="https://res.cloudinary.com/bottlebooks/image/upload/v2/vievinum/premiumpaket-1.png"
          style={{
            height: '57mm',
            width: '70mm',
            margin: '3mm',
            marginRight: 0,
            padding: '1mm',
            borderWidth: 1,
          }}
        />
      </Box>

      <Text variant="semiTitle">Matchmaking besser nutzen</Text>
      <Text>Nutze die Terminbuchung mit Fachbesuchern im Matchmaking.</Text>
      <Text variant="p">
        Wir legen deine VieVinum-Weine für dich im Matchmaking-Tool von b2match
        an, damit du für Fachbesucher über möglichst viele Wege gefunden wirst.
      </Text>

      <Text variant="semiTitle">
        Bonus: ProWein-Web&amp;App-Plus-Paket gratis
      </Text>
      <Text>
        Profitiere doppelt, falls du auch an der ProWein teilnimmst: Das
        Web&amp;App-Plus-Paket im Wert von 199€ ist kostenlos im Premiumeintrag
        inkludiert. Mehr Infos zum Paket auf der ProWein-Homepage unter
        https://oos.prowein.de/de/BOTT_BOT003
      </Text>

      <Text variant="semiTitle">Jetzt bestellen</Text>
      <Text variant="p">
        Bis zum 1. April 2022 gibt's den Premiumeintrag zum ermäßigten Preis von
        499€ statt 599€ (exkl. USt). Zur Bestellung einfach ein kurzes E-Mail an
        office@unserwein.at senden. Wir beantworten auch gerne all deine Fragen!
      </Text>
      <Text variant="p">
        Du hast bereits einen Premiumeintrag gebucht? Schreibe einfach ein
        kurzes E-Mail an office@unserwein.at, und wir schalten deinen Account
        gleich frei!
      </Text>
    </Page>
  );
}

/** @type {import('../../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"]} */
const theme = {
  colors: { primary: '#999', background: '#555' },
  components: {
    Text: {
      base: { ...dinLight },
      default: { fontSize: 12, lineHeight: 1.2 },
      p: { fontSize: 12, lineHeight: 1.2, marginBottom: '3mm' },
      semiTitle: {
        ...dinMedium,
        fontSize: 12,
        lineHeight: 1.2,
        marginTop: '5mm',
        marginBottom: '1mm',
      },
    },
    Title: {
      base: { ...dinMedium, textTransform: 'uppercase' },
      default: { fontSize: 18, lineHeight: 1, marginBottom: '3mm' },
    },
  },
};

VieVinumPreview.fragment = gql`
  fragment VieVinumPreview on RegistrationCatalogSection {
    registration {
      premiumeintrag: customFieldValue(key: "premiumeintrag") {
        __typename
        ... on YesNoFieldValue {
          value
        }
      }
      registeredProducts {
        nodes {
          stand: customFieldText(key: "stand")
        }
      }
    }
    ...VieVinumTable
    ...VieVinumMyListTable
    ...VieVinum_getStands
  }

  ${VieVinumTable.fragment}
  ${VieVinumMyListTable.fragment}
  ${getStands.fragment}
`;

/** @param {import('./@types/VieVinumPreview').VieVinumPreviewFragment} data */
function afterQuery(data) {
  const stands = getStands(
    {
      ...data,
      hatPremiumeintrag: premiumeintrag(data.registration),
    },
    data.registration.registeredProducts.nodes
  );
  /** @type {import('../helpers/rooms').Room | undefined} */
  const room = {
    name: '(Saal noch nicht bekannt)',
    colors: { primary: '#2dafe6' },
    ...stands?.[0]?.registration?.stand?.room,
  };
  return {
    stands,
    room,
  };
}

/** @param {import('../Catalog/@types/VieVinumTable').VieVinumTableFragment["registration"]} registration */
function premiumeintrag(registration) {
  if (registration.premiumeintrag?.__typename === 'YesNoFieldValue') {
    return registration.premiumeintrag.value;
  }
  return false;
}
